<div mat-dialog-title>
  <h2>{{ 'common.insurance' | translate }}</h2>
</div>

<mat-progress-bar mode="determinate"></mat-progress-bar>

<form
  fxLayout="column"
  fxLayoutGap="10px"
  [formGroup]="form"
  (ngSubmit)="onSubmit()"
  autocomplete="off"
>
  <mat-dialog-content>
    <div fxLayout="column">
      <mat-form-field>
        <mat-label>{{ 'dialog.insurance.insurance-type' | translate }}</mat-label>
        <mat-select formControlName="type">
          <mat-option [value]="PATIENT_INSURANCE_TYPE.PRIMARY">{{ 'insurances.type.P' | translate }}</mat-option>
          <mat-option [value]="PATIENT_INSURANCE_TYPE.SECONDARY">{{ 'insurances.type.S' | translate }}</mat-option>
          <mat-option [value]="PATIENT_INSURANCE_TYPE.TERTIARY">{{ 'insurances.type.T' | translate }}</mat-option>
        </mat-select>
      </mat-form-field>

      <ng-container *ngIf="hasAnotherActiveInsuranceWithSameType$ | async as insurance">
        <div class="text-center text-warn mb-3">{{ 'dialog.insurance.conflict' | translate: { type: ('insurances.type.' + form.value.type | translate).toLowerCase() } }}</div>

        <mat-form-field class="mb-3">
          <mat-label>{{ 'dialog.insurance.conflict-resolution.title' | translate }}</mat-label>
          <mat-select required formControlName="conflictResolution">
            <mat-option value="inactivate">{{ 'dialog.insurance.conflict-resolution.inactive' | translate: { carrier: insurance.clinicCarrierName } }}</mat-option>
            <mat-option *ngIf="form.value.type !== PATIENT_INSURANCE_TYPE.PRIMARY" value="changeTypeToPrimary">
              {{ 'dialog.insurance.conflict-resolution.changeTypeToPrimary' | translate: { carrier: insurance.clinicCarrierName } }}
              <span *ngIf="hasActiveInsurance(PATIENT_INSURANCE_TYPE.PRIMARY) as anotherConflict" class="text-warn">
                {{ 'dialog.insurance.conflict-resolution.another' | translate: { carrier: anotherConflict.clinicCarrierName } }}
              </span>
            </mat-option>
            <mat-option *ngIf="form.value.type !== PATIENT_INSURANCE_TYPE.SECONDARY" value="changeTypeToSecondary">
              {{ 'dialog.insurance.conflict-resolution.changeTypeToSecondary' | translate: { carrier: insurance.clinicCarrierName } }}
              <span *ngIf="hasActiveInsurance(PATIENT_INSURANCE_TYPE.SECONDARY) as anotherConflict" class="text-warn">
                {{ 'dialog.insurance.conflict-resolution.another' | translate: { carrier: anotherConflict.clinicCarrierName } }}
              </span>
            </mat-option>
            <mat-option *ngIf="form.value.type !== PATIENT_INSURANCE_TYPE.TERTIARY" value="changeTypeToTertiary">
              {{ 'dialog.insurance.conflict-resolution.changeTypeToTertiary' | translate: { carrier: insurance.clinicCarrierName } }}
              <span *ngIf="hasActiveInsurance(PATIENT_INSURANCE_TYPE.TERTIARY) as anotherConflict" class="text-warn">
                {{ 'dialog.insurance.conflict-resolution.another' | translate: { carrier: anotherConflict.clinicCarrierName } }}
              </span>
            </mat-option>
            <mat-divider></mat-divider>
            <mat-option class="text-warn" value="nothing">{{ 'dialog.insurance.conflict-resolution.nothing' | translate: { carrier: insurance.clinicCarrierName } }}</mat-option>
            <mat-option class="text-warn" value="delete">{{ 'dialog.insurance.conflict-resolution.delete' | translate: { carrier: insurance.clinicCarrierName } }}</mat-option>
          </mat-select>
          <mat-hint>{{ 'dialog.insurance.conflict-resolution.hint' | translate: { carrier: insurance.clinicCarrierName } }}</mat-hint>
        </mat-form-field>
      </ng-container>

      <mat-form-field class="mb-3">
        <mat-label>{{ 'dialog.insurance.carrier' | translate }}</mat-label>
        <input matInput required type="text" formControlName="lookup" [matAutocomplete]="autocompleteCarrier" />
        <mat-autocomplete #autocompleteCarrier="matAutocomplete" [displayWith]="displayCarrierAutocompleteLabel.bind(this)">
          <mat-option *ngFor="let lookup of filteredProviderLookups$ | async" [value]="lookup">
            {{ lookup.viewValue }}
          </mat-option>
        </mat-autocomplete>
        <mat-hint *ngIf="hasExistingCarrier">{{ 'dialog.insurance.carrier-locked' | translate }}</mat-hint>
        <mat-error *ngIf="form.get('lookup').invalid">
          {{ getErrorMessage('lookup') }}
        </mat-error>
      </mat-form-field>

      <ng-container *ngIf="notes">
        <mat-form-field fxFlex>
          <mat-label>Carrier Notes</mat-label>
          <textarea matInput disabled [(ngModel)]="notes" [ngModelOptions]="{standalone: true}"></textarea>
        </mat-form-field>
      </ng-container>

      <div
        fxLayout="row"
        fxLayoutGap="1rem"
        fxLayoutAlign="space-between start"
      >
        <mat-form-field appearance="fill" fxFlex>
          <mat-label>{{ 'dialog.insurance.member-id' | translate }}</mat-label>
          <input matInput placeholder="XX000123" formControlName="memberId" />
          <mat-error *ngIf="form.get('memberId').invalid">{{ getErrorMessage('memberId') }}</mat-error>
        </mat-form-field>

        <mat-form-field appearance="fill" fxFlex>
          <mat-label>{{ 'dialog.insurance.status' | translate }}</mat-label>
          <mat-select formControlName="status">
            <mat-option [value]="PATIENT_INSURANCE_STATUS.NotVerified">{{ 'dialog.insurance.not-verified' | translate }}</mat-option>
            <mat-option [value]="PATIENT_INSURANCE_STATUS.Active">{{ 'common.active' | translate }}</mat-option>
            <mat-option [value]="PATIENT_INSURANCE_STATUS.Inactive">{{ 'common.inactive' | translate }}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div
        fxLayout="row"
        fxLayoutGap="1rem"
        fxLayoutAlign="space-between start"
      >
        <mat-form-field appearance="fill" fxFlex>
          <mat-label>{{ 'dialog.insurance.group-id' | translate }}</mat-label>
          <input
            matInput
            placeholder="XX000123"
            formControlName="groupNumber"
          />
          <mat-error *ngIf="form.get('groupNumber').invalid">{{
            getErrorMessage('groupNumber')
          }}</mat-error>
        </mat-form-field>

        <mat-form-field appearance="fill" fxFlex>
          <mat-label>{{ 'dialog.insurance.group-name' | translate }}</mat-label>
          <input matInput formControlName="groupName" />
          <mat-error *ngIf="form.get('groupName').invalid">{{
            getErrorMessage('groupName')
          }}</mat-error>
        </mat-form-field>
      </div>

      <div
        fxLayout="row"
        fxLayoutGap="1rem"
        fxLayoutAlign="space-between start"
      >
        <!-- todo why the error message is for group number-->
        <mat-form-field appearance="fill" fxFlex>
          <mat-label>{{
            'dialog.insurance.effective-from' | translate
          }}</mat-label>
          <mmx-date-selector-input formControlName="effectiveFrom"></mmx-date-selector-input>
          <mat-error *ngIf="form.get('groupNumber').invalid">{{
            getErrorMessage('groupNumber')
          }}</mat-error>
        </mat-form-field>

        <mat-form-field appearance="fill" fxFlex>
          <mat-label>{{
            'dialog.insurance.effective-to' | translate
          }}</mat-label>
          <mmx-date-selector-input formControlName="effectiveTo"></mmx-date-selector-input>
          <mat-error *ngIf="form.get('groupName').invalid">{{
            getErrorMessage('groupName')
          }}</mat-error>
        </mat-form-field>
      </div>

      <div fxLayout="row" fxLayoutAlign="space-between center">
        <span *ngIf="lastVerifiedAt">
          Last verified on {{ lastVerifiedAt | momentDate: timezone ?? 'local' }}
          <ng-container *ngIf="lastVerifiedBy">
            by {{ lastVerifiedBy }}
          </ng-container>
        </span>

        <div fxFlex></div>

        <button mat-stroked-button type="button" (click)="updateLastVerifiedAt()" [disabled]="form.value.status !== PATIENT_INSURANCE_STATUS.Active">
          {{ 'dialog.insurance.update-last-verified' | translate }}
        </button>
      </div>
    </div>
  </mat-dialog-content>
  <mat-dialog-actions fxLayout="row" fxLayoutAlign="space-between center">
    <button mat-stroked-button mat-dialog-close type="button">
      {{ 'common.cancel' | translate }}
    </button>
    <button
      mat-flat-button
      color="primary"
      type="submit"
      [disabled]="form.invalid || isSaving || isLoading"
    >
      {{ 'common.save' | translate }}
    </button>
  </mat-dialog-actions>
</form>
